import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useColorMode } from "theme-ui";
import { setInnerLoaderFalse, setInnerLoaderTrue } from "../Redux/loaderSlice";

export const useThemeColor = () => {
    const dispatch = useDispatch();
    const [colorMode, setColorMode] = useColorMode();
    const [staticChangableData, setStaticChangableData] = useState(null);

    // Domain prefix for localStorage keys
    const domainPrefix = window.location.hostname?.replace(/[^a-zA-Z0-9]/g, "-");
    const prefixedThemeKey = `${domainPrefix}-theme-ui-color-mode`;
    const prefixedSystemThemeKey = `${domainPrefix}-system-theme-ui-color-mode`;

    const themeColor = localStorage.getItem(prefixedThemeKey) || null;
    const systemThemeColor = localStorage.getItem(prefixedSystemThemeKey) || null;

    // console.log(domainPrefix);

    /* ============================
       Load /changables/data.json
    ============================ */
    const getChangableData = useCallback(async () => {
        try {
            const res = await fetch("/changables/data.json", {
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                cache: "no-store",
            });
            const data = await res?.json();
            setStaticChangableData(data);

            if (!systemThemeColor) {
                localStorage.setItem(prefixedSystemThemeKey, data?.app_theme);
            }
        } catch (err) {
            console.error("Failed to fetch theme data:", err);
        }
    }, [systemThemeColor, prefixedSystemThemeKey]);

    /* ============================
       Load /themes/<domain>.json
    ============================ */
    const loadBrandTheme = useCallback(async () => {
        const host = window.location.hostname;
        const file = `${host}.json`;
        let themeJson;

        try {
            const res = await fetch(`/themes/${file}`, { cache: "no-store" });
            if (!res?.ok) throw new Error("not found");
            themeJson = await res?.json();
        } catch {
            const res = await fetch(`/themes/default.json`, { cache: "no-store" });
            themeJson = await res?.json();
        }

        const root = document.documentElement;
        Object.entries(themeJson?.colors)?.forEach(([key, value]) => {
            root.style.setProperty(`--${key}`, value);
        });

        window.APP_THEME = themeJson;
    }, []);

    /* ============================
       INITIAL LOAD
    ============================ */
    useEffect(() => {
        dispatch(setInnerLoaderTrue());
        Promise.all([getChangableData(), loadBrandTheme()])
            .finally(() => dispatch(setInnerLoaderFalse()));
    }, [dispatch, getChangableData, loadBrandTheme]);

    /* ============================
       APPLY SYSTEM / USER THEME
    ============================ */
    useEffect(() => {
        if (!staticChangableData?.app_theme) return;

        dispatch(setInnerLoaderTrue());

        // If system theme differs, apply new mode immediately
        if (staticChangableData?.app_theme !== systemThemeColor) {
            setColorMode(staticChangableData.app_theme);
            localStorage.setItem(prefixedThemeKey, staticChangableData?.app_theme);
            localStorage.setItem(prefixedSystemThemeKey, staticChangableData?.app_theme);
        } else if (!themeColor) {
            // first-time user
            setColorMode(staticChangableData?.app_theme);
            localStorage.setItem(prefixedThemeKey, staticChangableData?.app_theme);
        } else {
            // user previously selected
            setColorMode(themeColor);
        }

        dispatch(setInnerLoaderFalse());
    }, [
        staticChangableData?.app_theme,
        systemThemeColor,
        themeColor,
        setColorMode,
        dispatch,
        prefixedThemeKey,
        prefixedSystemThemeKey,
    ]);

    /* ============================
       TOGGLE LIGHT / DARK
    ============================ */
    const changeThemeMode = useCallback(() => {
        dispatch(setInnerLoaderTrue());

        const newColorMode = colorMode === "light" ? "dark" : "light";
        setColorMode(newColorMode);
        localStorage.setItem(prefixedThemeKey, newColorMode);

        dispatch(setInnerLoaderFalse());
    }, [colorMode, setColorMode, dispatch, prefixedThemeKey]);

    return {
        colorMode,
        changeThemeMode,
    };
};
